/**
 * 简化版知识图谱可视化组件
 *
 * 使用 cytoscape.js 可视化 Neo4j 知识图谱
 * 仅使用 cose 布局算法，适用于嵌入式场景
 * 支持紧凑模式和基础交互功能
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import cytoscape, { Core } from 'cytoscape';
import type { GraphData, GraphNode } from '../../types/knowledge';
import { knowledgeService } from '../../services/knowledge';

interface SimpleGraphViewerProps {
  workspaceId: number;
  height?: number;
  compact?: boolean;
  chapterNum?: number;
  entityId?: string;
  settingId?: number;
  className?: string;
}

const NODE_TYPE_COLORS: Record<string, { bg: string; border: string }> = {
  Character: { bg: '#3b82f6', border: '#1d4ed8' },
  Location: { bg: '#10b981', border: '#047857' },
  Item: { bg: '#f59e0b', border: '#b45309' },
  Event: { bg: '#8b5cf6', border: '#6d28d9' },
  '角色': { bg: '#3b82f6', border: '#1d4ed8' },
  '地点': { bg: '#10b981', border: '#047857' },
  '物品': { bg: '#f59e0b', border: '#b45309' },
  '事件': { bg: '#8b5cf6', border: '#6d28d9' },
};

const DEFAULT_COLORS = { bg: '#6B7280', border: '#4B5563' };

const LEGEND_TYPES = ['角色', '地点', '物品', '事件'];

const GraphLegend: React.FC = () => (
  <div className="absolute top-4 right-4 bg-white bg-opacity-95 border border-gray-300 rounded-lg shadow-sm p-2.5 z-10">
    <div className="text-xs font-medium text-gray-700 mb-1.5">实体类型</div>
    <div className="flex flex-col gap-1">
      {LEGEND_TYPES.map((type) => {
        const colors = NODE_TYPE_COLORS[type];
        return (
          <div key={type} className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-full border-2"
              style={{
                backgroundColor: colors.bg,
                borderColor: colors.border
              }}
            />
            <span className="text-xs text-gray-600">{type}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const COSE_LAYOUT_OPTIONS = {
  name: 'cose',
  animate: false,
  nodeDimensionsIncludeLabels: true,
  nodeRepulsion: 4000,
  idealEdgeLength: 100,
  edgeElasticity: 100,
  nestingFactor: 5,
  gravity: 1,
  numIter: 1000,
  initialTemp: 200,
  coolingFactor: 0.95,
  minTemp: 1.0
};

const Tooltip: React.FC<{
  node: GraphNode;
  onClose: () => void;
}> = ({ node, onClose }) => (
  <div className="absolute bg-white border border-gray-300 rounded-lg shadow-lg p-3 max-w-xs z-50">
    <div className="flex items-start justify-between mb-2">
      <h4 className="font-semibold text-gray-800">{node.name || node.label}</h4>
      <button
        onClick={onClose}
        className="text-gray-500 hover:text-gray-700"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    <div className="text-sm text-gray-600 space-y-1">
      <p><span className="font-medium">类型:</span> {node.type}</p>
      {node.summary && (
        <p className="text-xs text-gray-500 truncate">{node.summary.substring(0, 100)}</p>
      )}
    </div>
  </div>
);

export const SimpleGraphViewer: React.FC<SimpleGraphViewerProps> = ({
  workspaceId,
  height = 300,
  compact = false,
  chapterNum,
  entityId,
  settingId,
  className = ''
}) => {
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const cyRef = useRef<Core | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const sanitizeLabel = useCallback((text: string | undefined): string => {
    return (text || '').replace(/[\n\r\t]/g, ' ').trim().substring(0, 100);
  }, []);

  const loadGraphData = useCallback(async () => {
    if (!workspaceId || workspaceId <= 0) {
      setError('无效的工作区ID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let data;

      if (settingId) {
        data = await knowledgeService.getSettingRelatedGraph(workspaceId, settingId, 2);
      } else {
        const options: { limit?: number; chapter_num?: number } = {
          limit: compact ? 30 : 50
        };

        if (chapterNum) {
          options.chapter_num = chapterNum;
        }

        data = await knowledgeService.getGraphData(workspaceId, options);
      }

      if (entityId && data.nodes.length > 0) {
        const relatedNodeIds = new Set<string>();
        const targetNode = data.nodes.find(n => n.id === entityId);
        
        if (targetNode) {
          relatedNodeIds.add(entityId);
          data.edges.forEach(edge => {
            if (edge.source === entityId) relatedNodeIds.add(edge.target);
            if (edge.target === entityId) relatedNodeIds.add(edge.source);
          });
        }

        const filteredNodes = data.nodes.filter(n => relatedNodeIds.has(n.id));
        const filteredEdges = data.edges.filter(e => 
          relatedNodeIds.has(e.source) && relatedNodeIds.has(e.target)
        );

        setGraphData({
          nodes: filteredNodes,
          edges: filteredEdges,
          total: filteredNodes.length
        });
      } else {
        setGraphData(data);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '加载图谱数据失败';
      setError(message);
      console.error('[SimpleGraphViewer] 加载图谱数据失败:', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, compact, chapterNum, entityId, settingId]);

  useEffect(() => {
    if (!graphData || graphData.nodes.length === 0) return;

    let isMounted = true;
    let cyInstance: Core | null = null;
    let layoutInstance: ReturnType<Core['layout']> | null = null;

    const rafId = requestAnimationFrame(() => {
      if (!isMounted || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      if (cyRef.current) {
        try {
          cyRef.current.stop();
          cyRef.current.removeAllListeners();
          cyRef.current.destroy();
        } catch (e) {
          console.warn('[SimpleGraphViewer] Error destroying previous instance:', e);
        }
        cyRef.current = null;
      }

      const validNodeIds = new Set(graphData.nodes.filter(n => n.id).map(n => n.id));
      const elements = {
        nodes: graphData.nodes.filter(n => n.id).map((node) => {
          const backendData = node._cytoscapeData || {};
          const nodeSize = compact ? '30px' : '40px';
          const fontSize = compact ? '10px' : '12px';
          
          return {
            data: {
              id: node.id,
              type: node.type,
              summary: node.summary,
              mention_count: node.mention_count,
              bg: node.bg || (backendData.bg as string) || NODE_TYPE_COLORS[node.type as keyof typeof NODE_TYPE_COLORS]?.bg || DEFAULT_COLORS.bg,
              border: node.border || (backendData.border as string) || NODE_TYPE_COLORS[node.type as keyof typeof NODE_TYPE_COLORS]?.border || DEFAULT_COLORS.border,
              ...backendData,
              label: sanitizeLabel(node.label),
              name: sanitizeLabel(node.name || node.label),
              nodeSize,
              fontSize
            },
            position: node.position
          };
        }),
        edges: graphData.edges.filter(e => e.source && e.target && validNodeIds.has(e.source) && validNodeIds.has(e.target)).map((edge) => ({
          data: {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            label: edge.label,
            fact: edge.fact,
            since_chapter: edge.since_chapter,
            strength: edge.strength,
            valid_at: edge.valid_at,
            invalid_at: edge.invalid_at
          }
        }))
      };

      if (!isMounted) return;

      const cy = cytoscape({
        container: containerRef.current,
        elements,
        style: [
          {
            selector: 'node',
            style: {
              'background-color': 'data(bg)',
              'border-color': 'data(border)',
              'border-width': 2,
              'font-size': 'data(fontSize)',
              'text-valign': 'center',
              'text-halign': 'center',
              'label': 'data(label)',
              'width': 'data(nodeSize)',
              'height': 'data(nodeSize)',
              'shape': 'ellipse',
              'text-outline-color': 'data(border)',
              'text-outline-width': '2px'
            }
          },
          {
            selector: 'edge',
            style: {
              'width': 2,
              'line-color': '#64748b',
              'target-arrow-color': '#64748b',
              'target-arrow-shape': 'triangle',
              'curve-style': 'bezier',
              'label': 'data(label)',
              'font-size': '10px',
              'text-rotation': 'autorotate',
              'text-margin-y': -10,
              'color': '#475569'
            }
          }
        ],
        minZoom: 0.1,
        maxZoom: 3
      });

      if (!isMounted) {
        cy.destroy();
        return;
      }

      cyInstance = cy;
      cyRef.current = cy;

      const layout = cy.layout(COSE_LAYOUT_OPTIONS);
      layoutInstance = layout;
      layout.run();

      cy.on('tap', (evt) => {
        if (!isMounted || !cyInstance) return;
        const target = evt.target;
        if (target === cy) {
          setSelectedNode(null);
        } else if (target.isNode()) {
          const nodeData = target.data() as GraphNode;
          setSelectedNode(nodeData);
          
          const position = target.renderedPosition();
          const containerRect = containerRef.current?.getBoundingClientRect();
          if (containerRect) {
            setTooltipPosition({
              x: position.x + containerRect.left,
              y: position.y + containerRect.top
            });
          }
        }
      });
    });

    return () => {
      isMounted = false;
      cancelAnimationFrame(rafId);
      if (layoutInstance) {
        try {
          layoutInstance.stop();
        } catch (e) {
          // ignore
        }
        layoutInstance = null;
      }
      if (cyInstance) {
        try {
          cyInstance.stop();
          cyInstance.removeAllListeners();
          cyInstance.destroy();
        } catch (e) {
          console.warn('[SimpleGraphViewer] Error destroying instance:', e);
        }
        cyInstance = null;
      }
      if (cyRef.current === cyInstance) {
        cyRef.current = null;
      }
    };
  }, [graphData, compact, sanitizeLabel]);

  useEffect(() => {
    loadGraphData();
  }, [loadGraphData]);

  return (
    <div 
      className={`bg-white border border-gray-200 rounded-lg ${className}`}
      style={{ height: `${height}px` }}
    >
      <div className="relative w-full h-full">
        <div className="absolute inset-0 rounded-lg overflow-hidden">
          <div
            ref={containerRef}
            className="w-full h-full"
            style={{ backgroundColor: '#fafaf9' }}
          />

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80">
              <div className="text-sm text-gray-600">加载中...</div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          )}

          {!loading && !error && graphData && graphData.nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80">
              <div className="text-sm text-gray-600">暂无图谱数据</div>
            </div>
          )}
        </div>

        {selectedNode && (
          <div 
            className="fixed"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translate(-50%, -100%)',
              zIndex: 50
            }}
          >
            <Tooltip node={selectedNode} onClose={() => setSelectedNode(null)} />
          </div>
        )}

        {graphData && graphData.nodes.length > 0 && <GraphLegend />}
      </div>
    </div>
  );
};

export default SimpleGraphViewer;